import React, { useRef, useState } from "react";
import { message } from "antd";
import { AlertCircle, CheckCircle2, FileSpreadsheet, Loader2, RefreshCw, Sparkles, Trash2, Upload } from "lucide-react";
import {
  PRODUCT_FILE_ACCEPT,
  PRODUCT_FILE_MAX_BYTES,
  PRODUCT_MAX_RECORDS,
  parseProductFile,
} from "./productFileParser";
import { downloadDataUrl, slugify, validateProductRecords } from "./imageCreationService";

const cellCls =
  "w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-900 focus:border-indigo-500 focus:outline-none disabled:bg-gray-50";

const STATUS = {
  queued: { text: "Waiting", cls: "text-gray-500" },
  working: { text: "Creating…", cls: "text-indigo-600" },
  done: { text: "Done", cls: "text-green-700" },
  error: { text: "Failed", cls: "text-red-600" },
};

/**
 * Image Creation → "JSON / Excel" input method.
 * Upload → parse in the browser → server re-validates the records → merchant fixes any bad rows → generate one image per
 * row, one at a time (the server allows one generation per store at once), with a status and error per row.
 * `generateOne(record)` is supplied by ImageCreation (it knows the template, prompt and aspect ratio) and resolves to a
 * generated version; `onVersion(version)` adds it to the shared preview/download list.
 */
export default function ProductBatch({ disabled, generateOne, onVersion, canGenerate, blockedReason }) {
  const inputRef = useRef(null);
  const cancelRef = useRef(false);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]); // { record, errors: string[], status, error, versionKey }
  const [fileError, setFileError] = useState("");
  const [busy, setBusy] = useState(false); // parsing / validating
  const [running, setRunning] = useState(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const revalidate = async (nextRows) => {
    // Server-side check of the (possibly edited) records; the browser never sends the file itself.
    const data = await validateProductRecords(nextRows.map((r) => r.record));
    return nextRows.map((r, i) => ({ ...r, errors: data.records[i]?.errors || [] }));
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFileError("");
    setBusy(true);
    try {
      const records = await parseProductFile(file);
      const checked = await revalidate(records.map((record) => ({ record, errors: [], status: "queued", error: "" })));
      setRows(checked);
      setFileName(file.name);
    } catch (err) {
      setRows([]);
      setFileName("");
      setFileError(err.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const editField = (index, field, value) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, record: { ...r.record, [field]: value }, status: r.status === "done" ? "queued" : r.status } : r)));

  // Re-check the server's rules once the person leaves a field.
  const handleBlur = async () => {
    try {
      setRows(await revalidate(rowsRef.current));
    } catch (err) {
      message.error(err.message);
    }
  };

  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  const clearAll = () => {
    setRows([]);
    setFileName("");
    setFileError("");
  };

  const run = async (onlyFailed = false) => {
    if (running) return;
    cancelRef.current = false;
    setRunning(true);
    const pick = (r) => r.errors.length === 0 && r.status !== "done" && (!onlyFailed || r.status === "error");
    const todo = rowsRef.current.map((r, i) => (pick(r) ? i : -1)).filter((i) => i >= 0);
    setRows((prev) => prev.map((r, i) => (todo.includes(i) ? { ...r, status: "queued", error: "" } : r)));
    for (const index of todo) {
      if (cancelRef.current) break;
      const rec = rowsRef.current[index]?.record;
      if (!rec) continue;
      setRows((prev) => prev.map((r, i) => (i === index ? { ...r, status: "working", error: "" } : r)));
      try {
        const version = await generateOne(rec);
        onVersion(version);
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, status: "done", versionKey: version.key, imageDataUrl: version.imageDataUrl } : r)));
      } catch (err) {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, status: "error", error: err.message || "Couldn't create this image." } : r)));
      }
    }
    setRunning(false);
    if (cancelRef.current) message.info("Stopped. Finished images are kept.");
  };

  const validCount = rows.filter((r) => r.errors.length === 0).length;
  const invalidCount = rows.length - validCount;
  const doneCount = rows.filter((r) => r.status === "done").length;
  const failedCount = rows.filter((r) => r.status === "error").length;
  const pendingCount = rows.filter((r) => r.errors.length === 0 && r.status !== "done").length;
  const locked = disabled || running;

  return (
    <div className="flex flex-col gap-3" data-testid="product-batch">
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-gray-900">Product file</span>
        <span className="text-xs text-gray-500">
          JSON, .xlsx or .xls with name, price and description columns (image URL optional). Up to {PRODUCT_MAX_RECORDS} products,{" "}
          {PRODUCT_FILE_MAX_BYTES / (1024 * 1024)} MB. The file is read in your browser; its contents are never run.
        </span>
        <input ref={inputRef} type="file" accept={PRODUCT_FILE_ACCEPT} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="product-file-input" />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={locked || busy}
          className="inline-flex w-fit items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 disabled:opacity-50"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : rows.length ? <RefreshCw size={15} /> : <Upload size={15} />}
          {busy ? "Reading file…" : rows.length ? "Choose a different file" : "Choose JSON or Excel file"}
        </button>
        {fileError && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{fileError}</p>}
      </div>

      {rows.length > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1.5 font-medium text-gray-900">
              <FileSpreadsheet size={14} /> {fileName} — {rows.length} product{rows.length === 1 ? "" : "s"}
            </span>
            <span>
              {validCount} ready
              {invalidCount > 0 && <span className="text-red-600">, {invalidCount} need fixing</span>}
              {doneCount > 0 && <span className="text-green-700">, {doneCount} done</span>}
              {failedCount > 0 && <span className="text-red-600">, {failedCount} failed</span>}
            </span>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full min-w-[560px] text-left text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-2 py-1.5 font-medium">#</th>
                  <th className="px-2 py-1.5 font-medium">Name</th>
                  <th className="w-20 px-2 py-1.5 font-medium">Price</th>
                  <th className="px-2 py-1.5 font-medium">Description</th>
                  <th className="px-2 py-1.5 font-medium">Status</th>
                  <th className="w-8 px-1 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const st = r.errors.length ? { text: "Fix this row", cls: "text-red-600" } : STATUS[r.status];
                  return (
                    <React.Fragment key={i}>
                      <tr className={`border-t border-gray-100 align-top ${r.errors.length ? "bg-red-50/50" : ""}`}>
                        <td className="px-2 py-1.5 text-gray-400">{i + 1}</td>
                        <td className="px-2 py-1.5">
                          <input className={cellCls} aria-label={`Name, row ${i + 1}`} value={r.record.name} maxLength={200} disabled={locked} onChange={(e) => editField(i, "name", e.target.value)} onBlur={handleBlur} />
                        </td>
                        <td className="px-2 py-1.5">
                          <input className={cellCls} aria-label={`Price, row ${i + 1}`} value={r.record.price} maxLength={60} disabled={locked} onChange={(e) => editField(i, "price", e.target.value)} onBlur={handleBlur} />
                        </td>
                        <td className="px-2 py-1.5">
                          <input className={cellCls} aria-label={`Description, row ${i + 1}`} value={r.record.description} maxLength={600} disabled={locked} onChange={(e) => editField(i, "description", e.target.value)} onBlur={handleBlur} />
                          {r.record.imageUrl && <span className="mt-0.5 block truncate text-[11px] text-gray-400" title={r.record.imageUrl}>Image link: {r.record.imageUrl}</span>}
                        </td>
                        <td className={`whitespace-nowrap px-2 py-1.5 font-medium ${st.cls}`}>
                          <span className="inline-flex items-center gap-1">
                            {r.status === "working" && !r.errors.length && <Loader2 size={12} className="animate-spin" />}
                            {r.status === "done" && !r.errors.length && <CheckCircle2 size={12} />}
                            {r.status === "error" && !r.errors.length && <AlertCircle size={12} />}
                            {st.text}
                          </span>
                          {r.status === "done" && r.imageDataUrl && (
                            <button type="button" className="ml-2 font-normal text-indigo-600 underline" onClick={() => downloadDataUrl(r.imageDataUrl, `${slugify(r.record.name) || "menu-image"}.png`)}>
                              Download
                            </button>
                          )}
                        </td>
                        <td className="px-1 py-1.5">
                          <button type="button" aria-label={`Remove row ${i + 1}`} disabled={locked} onClick={() => removeRow(i)} className="text-gray-400 hover:text-red-600 disabled:opacity-40">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                      {(r.errors.length > 0 || r.error) && (
                        <tr className="bg-red-50/50">
                          <td />
                          <td colSpan={5} className="px-2 pb-1.5 text-[11px] text-red-700">
                            {r.errors.length ? r.errors.join(" ") : r.error}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {blockedReason && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{blockedReason}</p>}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => run(false)}
              disabled={running || disabled || !canGenerate || pendingCount === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {running ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {running ? "Creating images…" : `Generate ${pendingCount} image${pendingCount === 1 ? "" : "s"}`}
            </button>
            {running && (
              <button type="button" onClick={() => (cancelRef.current = true)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-400">
                Stop after this one
              </button>
            )}
            {!running && failedCount > 0 && (
              <button type="button" onClick={() => run(true)} disabled={disabled || !canGenerate} className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-400 disabled:opacity-50">
                Retry failed ({failedCount})
              </button>
            )}
            {!running && (
              <button type="button" onClick={clearAll} className="text-sm font-medium text-gray-500 hover:text-gray-700">
                Clear
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">Images are made one at a time. Finished images appear in the preview on the right, where you can download each one.</p>
        </>
      )}
    </div>
  );
}
